<template>
  <div class="wrapper relative w-full h-full flex flex-1">
    <model-viewer
      v-if="feetObjUrl"
      v-bind="feetViewerProps"
      :obj-url="feetObjUrl"
      :measurement="currentMeasurement"
      :show-feet="feetToShow"
      :style="`height: ${containerHeight[0]}`"
    />
    <div v-if="!measurementFailed" class="back md:mt-10 md:mr-10">
      <img class="md:w-8 md:h-8" src="@/assets/common/close-icon.svg" @click="leave" />
    </div>
    <panel v-if="!measurementFailed" :height="containerHeight[1]">
      <div class="w-full h-full text-center flex flex-col purple-bg p-10 flex-centered">
        <p
          class="scan-result-message"
          v-html="nothingYet ? $t('result.analyzing') : $t('result.scan_successful')"
        ></p>
        <vue-qr v-if="isDAF && hasMeasurements" class="my-4" v-bind="dafQrSettings" />
      </div>
    </panel>
    <rounded-card v-else no-head :height="panelHeight">
      <scan-error :error="retryFailed" :message="failureMessage" @retry="retry" @close="leave" />
    </rounded-card>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import VueQr from 'vue-qr';

import ScanProcess from '@/modules/scan-process';
import { getCurrentBreakpoint } from '@/modules/utils';
import measurements from '@/mixins/feet-measurements';
import reinitializeScan from '@/mixins/reinitialize-scan';

import ModelViewer from '@/components/common/ModelViewer.vue';
import Panel from '@/components/common/Panel.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';
import ScanError from '@/components/FootScan/ScanError.vue';

export default {
  name: 'ShowResult',
  mixins: [measurements, reinitializeScan],
  components: {
    ModelViewer,
    Panel,
    RoundedCard,
    ScanError,
    VueQr,
  },
  async mounted() {
    const { tid } = ScanProcess;
    this.tid = tid;
    await Promise.all([this.getMeasurements(), ScanProcess.release()]);
    this.ready = true;
  },
  data: () => ({
    retryFailed: false,
    failureMessage: '',
    tid: '',
  }),
  methods: {
    async getMeasurements() {
      try {
        const data = await ScanProcess.waitMeasurement(this.tid);
        this.processMeasurements(data);
      } catch (e) {
        console.log(`[ERROR] ${e.message || 'scan failed for unknown reason'}`);
        this.measurementFailed = true;
      }
    },
    toggleLRFoot(footIndex) {
      const value = this.feetToShow[footIndex];
      this.$set(this.feetToShow, footIndex, !value);
    },
    async retry() {
      if (this.nothingYet) return;

      try {
        this.$events.startLoading();
        await this.reInitializeScan();
        this.$events.goto('FootScan/Scan');
      } catch (e) {
        this.showError(e.message);
      } finally {
        this.$events.stopLoading();
      }
    },
    leave() {
      if (this.nothingYet) return;

      this.$events.goto('End');
    },
    showError(message) {
      this.retryFailed = true;
      this.failureMessage = message;
    },
  },
  computed: {
    ...mapGetters('app', ['isDAF']),
    ...mapGetters('user', ['username']),
    ...mapState('user', ['feetObjUrl']),
    nothingYet() {
      return !this.measurementFailed && !this.hasMeasurements;
    },
    dafQrSettings() {
      if (!this.isDAF) return {};

      return {
        text: `${this.username},${this.tid}`,
        size: getCurrentBreakpoint() === 'xs' ? 150 : 200,
        margin: 10,
      };
    },
    panelHeight() {
      if (this.measurementFailed) return 'auto';

      if (this.isStamp) return 350;

      return '15%';
    },
    containerHeight() {
      return this.measurementFailed ? ['89%', '85%'] : ['89%', this.isDAF ? '33%' : '19%'];
    },
  },
};
</script>

<style lang="scss" scoped>
%safe-top {
  @screen xs {
    $margin: 1rem;
    margin-top: calc(constant(safe-area-inset-top, #{-1 * $margin}) + #{$margin});
    margin-top: calc(env(safe-area-inset-top, #{-1 * $margin}) + #{$margin});
  }
}

.wrapper {
  ::v-deep {
    .threeViewer {
      background: rgb(255, 255, 255);
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.23) 0%,
        rgba(142, 126, 189, 0.75) 100%
      );
    }
    .unit-switcher-root {
      background: white;
    }
  }
  .back {
    @apply absolute flex top-0 right-0 mx-auto mr-4 items-center px-4 py-2;
    @extend %safe-top;
  }
}
.feet-control {
  @apply absolute flex justify-center items-center top-0 left-0 right-0 mx-auto;
  @extend %safe-top;
  .lr-toggler {
    @apply inline-flex justify-center items-center mx-4 text-white rounded-3xl;
    border: 1px solid white;
    width: 26px;
    height: 26px;
    font-size: 11px;
    &.active {
      @apply bg-white text-black;
    }
  }
}
.purple-bg {
  background-image: linear-gradient(to right, #2245ff, #540deb);
  color: #fff;
  .scan-result-message {
    @apply h-full;
    color: #fff;
    font-weight: 600;
    line-height: 1.7;
    font-size: 20px;
  }
}
</style>
